import React, { useState, useEffect } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import LabelledTextField from '../components/LabelledTextField';
import { dateOptions, dateTimeOptions } from '../utils/dates';
import { getValveExercise, getValveExerciseLatest, deleteValveExercise, getValve } from '../services/api';
import ValveExerciseModal from '../components/valveExercises/ValveExerciseModal';
import IsAuthorized from '../services/authentication/IsAuthorized';
import { makeValve, makeValveExercise } from '../models';
import ExerciseChart from '../components/ExerciseChart';
import CircleButton from '../components/CircleButton';
import { faPrint } from '@fortawesome/free-solid-svg-icons';

const ValveExercise = () => {
  const [exercise, setExercise] = useState(null);
  const [valve, setValve] = useState(null);
  const [loadingExercise, setLoadingExercise] = useState(false);
  const [loadingValve, setLoadingValve] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [startDelete, setStartDelete] = useState(false);
  const { valveId, valveExerciseId } = useParams();
  const history = useHistory();

  useEffect(() => {
    setupValve();
    setupExercise();
  }, [valveId, valveExerciseId, history.location]);

  const setupValve = async () => {
    try {
      setLoadingValve(true);
      const result = await getValve(valveId);
      console.log(result);
      setValve(makeValve(result));
      setLoadingValve(false);
    } catch (ex) {
      setExercise(null);
      setLoadingValve(false);
    }
  };

  const setupExercise = async () => {
    try {
      setLoadingExercise(true);
      const result = valveExerciseId ? await getValveExercise(valveExerciseId) : await getValveExerciseLatest(valveId);
      setExercise(makeValveExercise(result));
      setLoadingExercise(false);
    } catch (ex) {
      setExercise(null);
      setLoadingExercise(false);
    }
  };

  const toggleModal = () => setModalOpen(!modalOpen);

  const selectExercise = (exerciseId) => {
    history.push(`/valves/${valveId}/valveExercise/${exerciseId}`);
  };

  const deleteRecord = async () => {
    try {
      setStartDelete(false);
      await deleteValveExercise(exercise.id);
      history.replace(`/valves/${valveId}/valveExercise`);
    } catch (ex) {
      console.log(ex);
      history.replace(`/valves/${valveId}/valveExercise`);
    }
  };

  const exerciseDate = new Date(exercise?.exerciseDate ?? null);
  const exerciseDateString = new Intl.DateTimeFormat('en-US', dateOptions).format(exerciseDate);
  
  return (
    <div className="valve-exercise">
      <div className="report-title">
        <div className="title">
          <h3>Valve Exercise</h3>
          <div className="form-links no-print">
            <div className='link' onClick={toggleModal}>Change Exercise</div>
            <Link to={`/reports/history/${valveId}`} target='_blank' className='link'>View Exercise History</Link>
          </div>
        </div>
        {loadingValve || loadingExercise || <CircleButton icon={faPrint} size="large" title="Print" onClick={print} className="no-print" />}
      </div>
      {loadingExercise && <div className="spinner"></div>}
      {!loadingExercise && exercise && (
        <>          
          <div className="result-details">
            <div className="result-detail">
              <label>Device Type:</label>
              <span>Valve</span>
            </div>
            <div className="result-detail">
              <label>Device ID:</label>
              <span>{valve?.name}</span>
            </div>
          </div>
          <div className="valve-settings">
            <div className="valve-setting">
              <label>Set Rev:</label>
              <span>{exercise?.revolutions}</span>
            </div>
            <div className="valve-setting">
              <label>Set Torque:</label>
              <span>{exercise?.torqueSetPoint}</span>
            </div>
            <div className="valve-setting">
              <label>Set Speed:</label>
              <span>{exercise?.rpmSetPoint}</span>
            </div>
          </div>
          <div className="valve-location">
            <div className="valve-location-info">
              <div className="valve-location-detail">
                <label>Address:</label>
                <span>{((valve?.streetNumber ?? '') + ' ' + (valve?.streetName ?? '')).trim()}</span>
              </div>
              <div className="valve-location-detail">
                <label>Cross Street:</label>
                <span>{valve?.crossStreet}</span>
              </div>
              <div className="valve-location-detail">
                <label>Location:</label>
                <span>{valve?.locationNotes}</span>
              </div>
              <div className="valve-location-detail">
                <label>District:</label>
                <span>{valve?.district}</span>
              </div>
              <div className="valve-location-detail">
                <label>Sub-Division:</label>
                <span>{valve?.subDivision}</span>
              </div>
            </div>
            <div className="valve-gps-info">
              <div className="valve-gps-detail">
                <label>Latitude:</label>
                <span>{valve?.latitude?.toFixed(6)}</span>
              </div>
              <div className="valve-gps-detail">
                <label>Longitude:</label>
                <span>{valve?.longitude?.toFixed(6)}</span>
              </div>
              <div className="valve-gps-detail">
                <label>Elevation:</label>
                <span>{valve?.elevation?.toFixed(2)}</span>
              </div>
              <div className="valve-gps-detail">
                <label>State X / Y:</label>
                <span>{valve?.stateX} / {valve?.stateY}</span>
              </div>
            </div>
          </div>
          <div className="valve-info">
            <div className="valve-info-detail">
              <label>Manufacturer:</label>
              <span>{valve?.manufacturer}</span>
            </div>
            <div className="valve-info-detail">
              <label>Install Date:</label>
              <span>{valve?.installDate ? new Date(valve.installDate).toLocaleDateString('en-US', dateOptions) : ''}</span>
            </div>
            <div className="valve-info-detail">
              <label>Status:</label>
              <span>{valve?.status}</span>
            </div>
            <div className="valve-info-detail">
              <label>Valve Size:</label>
              <span>{valve?.size?.toFixed(2)}</span>
            </div>
            <div className="valve-info-detail">
              <label>Open Direction:</label>
              <span>{exercise?.openDirection}</span>
            </div>
            <div className="valve-info-detail">
              <label>Bury Depth:</label>
              <span>{valve?.boxDepth?.toFixed(2)}</span>
            </div>
          </div>
          {exercise.manual ? (
            <div className="exercise-stats">
              <div className="exercise-stat">
                <label>Exercise Date:</label>
                <span>{exercise?.exerciseDate ? new Date(exercise.exerciseDate).toLocaleDateString('en-US', dateTimeOptions) : ''}</span>
              </div>
              <div className="exercise-stat">
                <label>Difficulty:</label>
                <span>{exercise?.difficulty}</span>
              </div>
              <div className="exercise-stat">
                <label>Comments:</label>
                <span>{exercise?.comments}</span>
              </div>
            </div>
          ) : (
            <div className="exercise-stats">
              <div className="exercise-stat">
                <label>Exercise Date:</label>
                <span>{exercise?.exerciseDate ? new Date(exercise.exerciseDate).toLocaleDateString('en-US', dateTimeOptions) : ''}</span>
              </div>
              <div className="exercise-stat">
                <label>Total Revs:</label>
                <span>{exercise?.totalRevolutions}</span>
              </div>
              <div className="exercise-stat">
                <label>Cycle Count:</label>
                <span>{exercise?.autoCycles}</span>
              </div>
              <div className="exercise-stat">
                <label>Max Torque:</label>
                <span>{exercise?.maxTorque}</span>
              </div>
              <div className="exercise-stat">
                <label>Revs @ Max Torque:</label>
                <span>{exercise?.maxTorqueRevolutions}</span>
              </div>
              <div className="exercise-stat">
                <label>Max Rev In Set Dir:</label>
                <span>{exercise?.maxRevolutions}</span>
              </div>
              <div className="exercise-stat">
                <label>Comments:</label>
                <span>{exercise?.comments}</span>
              </div>
            </div>
          )}
          {!startDelete ? (
            <div className="form-actions no-print">
              <IsAuthorized>
                <Link to={`/valves/${valveId}/valveExercise/${exercise.id}/edit`}>Edit</Link>
                <div className="link" onClick={() => setStartDelete(true)}>Delete</div>
              </IsAuthorized>
            </div>
          ) : (
            <div className="form-actions no-print">
              <span>Are you sure you want to delete this record?</span>
              <button type="button" className="btn btn-primary" onClick={deleteRecord}>Yes</button>
              <button type="button" className="btn btn-secondary" onClick={() => setStartDelete(false)}>No</button>
            </div>
          )}
          {!exercise.manual && <ExerciseChart exercise={exercise} />}
        </>
      )}
      {!loadingExercise && !exercise && (
        <div className="empty-body">
          <div className="empty-text">No Exercise Found</div>
          <div className="empty-text-small">We couldn't find an exercise.</div>
        </div>
      )}
      <ValveExerciseModal valveId={valveId} open={modalOpen} toggle={toggleModal} onSelect={selectExercise} valveExercise={exercise} />
    </div>
  );
};

export default ValveExercise;